import React from 'react';
import { PieChart } from '@mui/x-charts/PieChart';

interface PaymentMethodData {
  payment_method: string;
  total_payments: number;
  total_amount: number;
}

interface PaymentMethodsChartProps {
  paymentMethods: PaymentMethodData[];
}

export default function PaymentMethodsChart({ paymentMethods = [] }: PaymentMethodsChartProps) {
  if (!paymentMethods.length) {
    return (
      <div className="boxIndicator" style={{ flex: '1', minWidth: '0' }}>
        <h3>Medios de Pago Utilizados</h3>
        <div className="boxIndicatorContent" style={{ padding: '2rem', textAlign: 'center' }}>
          <p>No hay datos disponibles para el período seleccionado</p>
        </div>
      </div>
    );
  }

  // Prepare data for MUI PieChart
  const chartData = paymentMethods.map((method, index) => ({
    id: index,
    value: Number(method.total_amount),
    label: method.payment_method,
    // Color palette for payment methods
    color: [
      '#22c55e', // Green
      '#3b82f6', // Blue  
      '#f59e0b', // Amber
      '#ef4444', // Red
      '#8b5cf6', // Purple
      '#06b6d4', // Cyan
      '#f97316', // Orange
      '#84cc16', // Lime
      '#ec4899', // Pink
      '#6b7280', // Gray
    ][index % 10]
  }));

  // Calculate total for percentage display
  const total = paymentMethods.reduce((sum, method) => sum + Number(method.total_amount), 0);

  return (
    <div className="boxIndicator" style={{ flex: '1', minWidth: '0' }}>
      <h3>Medios de Pago Utilizados</h3>
      <div className="boxIndicatorContent" style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center'}}>
          {/* Chart */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: '250px' }}>
            <PieChart
              series={[
                {
                  data: chartData,
                  innerRadius: 60,
                  outerRadius: 100,
                  paddingAngle: 2,
                  cornerRadius: 5,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                  valueFormatter: (value) => `$${value.value?.toLocaleString() || 0}`,
                },
              ]}
              width={280}
              height={240}
              margin={{ left: 10, right: 10, top: 10, bottom: 10 }}
              slotProps={{
                legend: {
                  hidden: true,
                },
              }}
            />
          </div>

          {/* Legend with details */}
          <div style={{ flex: 1, minWidth: '180px' }}>
            <h4 style={{ margin: '0 0 1rem 0', fontSize: '14px', fontWeight: 'bold' }}>
              Desglose por Método
            </h4>
            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
              {paymentMethods.map((method, index) => {
                const percentage = total > 0 ? ((Number(method.total_amount) / total) * 100).toFixed(1) : 0;
                return (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '6px',
                      padding: '6px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      fontSize: '11px'
                    }}
                  >
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: chartData[index].color,
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                        {method.payment_method}
                      </div>
                      <div style={{ color: '#6b7280' }}>
                        {method.total_payments} pago{method.total_payments !== 1 ? 's' : ''}
                      </div>
                      <div style={{ fontWeight: 'bold', color: '#059669' }}>
                        ${Number(method.total_amount).toLocaleString()} ({percentage}%)
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Total */}
            <div style={{ 
              marginTop: '1rem', 
              padding: '6px', 
              backgroundColor: '#e5f3ff', 
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 'bold'
            }}>
              <div style={{ color: '#0369a1' }}>
                Total Recaudado: ${total.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
